import type { ElementPosition, ViewportInfo } from '../types/observations';

/**
 * Check if an element is visible in the viewport
 */
export function isElementVisible(element: Element): boolean {
  if (!(element instanceof HTMLElement)) {
    return false;
  }

  // Get computed style first
  const style = window.getComputedStyle(element);

  // Check display, visibility, and opacity
  if (style.display === 'none' || style.visibility === 'hidden' || style.opacity === '0') {
    return false;
  }

  // Check if element has dimensions
  const rect = element.getBoundingClientRect();
  if (rect.width === 0 || rect.height === 0) {
    return false;
  }

  // For fixed/sticky/absolute positioned elements, offsetParent can be null even if visible
  // So we skip the offsetParent check for positioned elements
  const position = style.position;
  const isPositioned = position === 'fixed' || position === 'sticky' || position === 'absolute';

  // Only check offsetParent for non-positioned elements
  if (!isPositioned && !element.offsetParent && element.tagName !== 'BODY') {
    // Double-check: even if offsetParent is null, element might still be visible
    // This can happen in shadow DOM or certain container situations
    // If we got this far (has dimensions, not display:none), trust that it's visible
    return rect.width > 0 && rect.height > 0;
  }

  return true;
}

/**
 * Check if an element is enabled (not disabled)
 */
export function isElementEnabled(element: Element): boolean {
  if (element instanceof HTMLInputElement ||
      element instanceof HTMLSelectElement ||
      element instanceof HTMLTextAreaElement ||
      element instanceof HTMLButtonElement) {
    return !element.disabled;
  }
  return true;
}

/**
 * Check if an element is currently in the viewport
 */
export function isElementInViewport(element: Element): boolean {
  const rect = element.getBoundingClientRect();
  return (
    rect.top >= 0 &&
    rect.left >= 0 &&
    rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
    rect.right <= (window.innerWidth || document.documentElement.clientWidth)
  );
}

/**
 * Get element position and size
 */
export function getElementPosition(element: Element): ElementPosition {
  const rect = element.getBoundingClientRect();
  return {
    x: rect.left + window.scrollX,
    y: rect.top + window.scrollY,
    width: rect.width,
    height: rect.height
  };
}

/**
 * Get viewport information
 */
export function getViewportInfo(): ViewportInfo {
  return {
    width: window.innerWidth,
    height: window.innerHeight,
    scrollX: window.scrollX,
    scrollY: window.scrollY
  };
}

/**
 * Get element's visible text content
 */
export function getElementText(element: Element): string {
  if (element instanceof HTMLInputElement || element instanceof HTMLTextAreaElement) {
    return element.value;
  }

  if (element instanceof HTMLSelectElement) {
    return element.options[element.selectedIndex]?.text || '';
  }

  return element.textContent?.trim() || '';
}

/**
 * Get element's value
 */
export function getElementValue(element: Element): string | undefined {
  if (element instanceof HTMLInputElement || element instanceof HTMLTextAreaElement) {
    return element.value;
  }

  if (element instanceof HTMLSelectElement) {
    return element.value;
  }

  return undefined;
}

/**
 * Get all attributes of an element
 */
export function getElementAttributes(element: Element): Record<string, string> {
  const attributes: Record<string, string> = {};

  for (let i = 0; i < element.attributes.length; i++) {
    const attr = element.attributes[i];
    attributes[attr.name] = attr.value;
  }

  return attributes;
}

/**
 * Check if element is interactive
 */
export function isInteractiveElement(element: Element): boolean {
  const tagName = element.tagName.toLowerCase();

  // Interactive tags
  if (['a', 'button', 'input', 'select', 'textarea'].includes(tagName)) {
    return true;
  }

  // Elements with click handlers or tabindex
  if (element.hasAttribute('onclick') || element.hasAttribute('tabindex')) {
    return true;
  }

  // Elements with role indicating interactivity
  const role = element.getAttribute('role');
  if (role && ['button', 'link', 'menuitem', 'tab', 'checkbox', 'radio'].includes(role)) {
    return true;
  }

  return false;
}

/**
 * Get element type classification
 */
export function getElementType(element: Element): string {
  const tagName = element.tagName.toLowerCase();

  if (tagName === 'button') return 'button';
  if (tagName === 'a') return 'link';
  if (tagName === 'img') return 'image';
  if (tagName === 'select') return 'select';
  if (tagName === 'textarea') return 'textarea';

  if (tagName === 'input') {
    const type = (element as HTMLInputElement).type;
    if (type === 'checkbox') return 'checkbox';
    if (type === 'radio') return 'radio';
    if (type === 'submit' || type === 'button') return 'button';
    return 'input';
  }

  return 'other';
}

/**
 * Wait for element to appear in DOM
 */
export function waitForElement(
  selector: string,
  timeout = 5000
): Promise<Element> {
  return new Promise((resolve, reject) => {
    const element = document.querySelector(selector);
    if (element) {
      resolve(element);
      return;
    }

    const observer = new MutationObserver(() => {
      const element = document.querySelector(selector);
      if (element) {
        observer.disconnect();
        resolve(element);
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });

    setTimeout(() => {
      observer.disconnect();
      reject(new Error(`Element not found: ${selector} (timeout: ${timeout}ms)`));
    }, timeout);
  });
}

/**
 * Scroll element into view
 */
export function scrollIntoView(element: Element, behavior: ScrollBehavior = 'smooth'): void {
  element.scrollIntoView({
    behavior,
    block: 'center',
    inline: 'center'
  });
}
